import { useState, useEffect } from "react";
import { modulesAPI, usersAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";

export default function LearningPathManager() {
  const { user } = useAuth();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ path_name: "", description: "", team_id: "" });
  const [teams, setTeams] = useState([]);

  useEffect(() => { loadPaths(); loadTeams(); }, []);

  const loadTeams = async () => {
    try {
      const res = await usersAPI.getTeams();
      if (res && res.data) setTeams(res.data);
    } catch (e) {
      console.warn("Could not load teams:", e);
    }
  };

  const loadPaths = async () => {
    try {
      const res = await modulesAPI.getPaths();
      setPaths(res.data);
    } catch { setError("Failed to load learning paths."); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.path_name.trim()) { setError("Learning path name is required."); return; }
    try {
      await modulesAPI.createPath({...form, team_id: form.team_id || null});
      setSuccess(`Learning path "${form.path_name}" created successfully.`);
      setOpen(false);
      setForm({ path_name: "", description: "", team_id: "" });
      loadPaths();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create learning path.");
    }
  };

  const handleDeletePath = async (pathId, pathName) => {
    if (!window.confirm(`Are you sure you want to delete "${pathName}" and ALL its modules? This cannot be undone.`)) return;
    try {
      await modulesAPI.deletePath(pathId);
      setSuccess(`Learning path "${pathName}" deleted successfully.`);
      loadPaths();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete learning path.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a237e">Learning Paths</Typography>
          <Typography variant="body2" color="text.secondary">Manage training programmes</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}
          sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
          New Learning Path
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : paths.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: "center", p: 4 }}>
          <LibraryBooksIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No learning paths yet</Typography>
          <Typography variant="body2" color="text.secondary">Create your first learning path to get started</Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {paths.map(path => (
            <Grid item xs={12} md={6} key={path.path_id}>
              <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>{path.path_name}</Typography>
                    <Chip label={path.is_active ? "Active" : "Inactive"}
                      color={path.is_active ? "success" : "default"} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {path.description || "No description"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip label={`${path.modules?.length || 0} modules`} size="small" variant="outlined" />
                    {user?.role === "admin" && (
                      <Button size="small" color="error" variant="outlined"
                        onClick={() => handleDeletePath(path.path_id, path.path_name)}
                        sx={{ ml: 1 }}>
                        Delete
                      </Button>
                    )}
                    {path.team_id ? (
                      <Chip label={teams.find(t => t.team_id === path.team_id)?.team_name || "Team"} 
                        size="small" color="primary" />
                    ) : (
                      <Chip label="All Teams" size="small" color="default" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Path ID: {path.path_id}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Create Learning Path</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Path Name" value={form.path_name}
            onChange={e => setForm({...form, path_name: e.target.value})}
            required sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Description" value={form.description}
            onChange={e => setForm({...form, description: e.target.value})}
            multiline rows={3} sx={{ mb: 2 }} />
          <FormControl fullWidth>
            <InputLabel>Assign to Team (optional)</InputLabel>
            <Select value={form.team_id} label="Assign to Team (optional)"
              onChange={e => setForm({...form, team_id: e.target.value})}>
              <MenuItem value="">All Teams (no restriction)</MenuItem>
              {(user?.role === "admin" ? teams : teams.filter(t => t.team_name === user?.team_name)).map(t => (
                <MenuItem key={t.team_id} value={t.team_id}>{t.team_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
